import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import { Camera, Check, Loader2, Plus, Search, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { camerasApi, cameraGroupsApi } from '@/api/endpoints'
import { toast } from '@/lib/toast'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { cn } from '@/lib/utils'
import type { CameraGroup } from '@/types'

/**
 * GroupMembersManager (#104) — one coherent surface for managing which cameras
 * belong to a group. Replaces the disorienting "filter-flip into select-mode"
 * flow. Owner-only: add/remove require group ownership server-side, so callers
 * must only open this for groups where `is_owner !== false`.
 *
 * Single-group model: a camera is in at most one group, so adding a camera that
 * already has a group MOVES it (the backend `add-cameras` endpoint performs the
 * move). The picker flags that with a "⚠ moves here" annotation.
 *
 * `group` is the LIVE object from the caller's camera-groups query (looked up by
 * id), so member rows refresh after a mutation invalidates the query.
 */
export function GroupMembersManager({
  group,
  onClose,
}: {
  group: CameraGroup | null
  onClose: () => void
}) {
  const { t } = useTranslation('groups')
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [toAdd, setToAdd] = useState<Set<number>>(new Set())

  // Reset transient state whenever the target group changes (incl. open/close).
  useEffect(() => {
    setSearch('')
    setToAdd(new Set())
  }, [group?.id])

  // The user's full camera set (owned + shared); page_size=1000 covers any
  // realistic single-owner fleet. Only fetched while the dialog is open.
  const { data: allCameras = [] } = useQuery({
    queryKey: queryKeys.cameras.all(),
    queryFn: () => camerasApi.getAll(),
    ...queryOptions.cameras.all,
    enabled: group !== null,
  })

  const members = group?.cameras_details ?? []
  const memberIds = useMemo(() => new Set(members.map((c) => c.id)), [members])

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase()
    return allCameras
      .filter((c) => !memberIds.has(c.id))
      .filter((c) => !q || c.name.toLowerCase().includes(q))
  }, [allCameras, memberIds, search])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
    queryClient.invalidateQueries({ queryKey: ['cameras'] })
  }

  // groupId is passed explicitly so an in-flight mutation never reads a nulled group.
  const addMutation = useMutation({
    mutationFn: ({ groupId, ids }: { groupId: number; ids: number[] }) =>
      cameraGroupsApi.addCameras(groupId, ids),
    onSuccess: (_data, { ids }) => {
      toast.success(t('membersManager.added', { count: ids.length }))
      setToAdd(new Set())
      invalidate()
    },
    onError: () => toast.error(t('membersManager.addFailed')),
  })

  const removeMutation = useMutation({
    mutationFn: ({ groupId, id }: { groupId: number; id: number }) =>
      cameraGroupsApi.removeCameras(groupId, [id]),
    onSuccess: () => {
      toast.success(t('membersManager.removed'))
      invalidate()
    },
    onError: () => toast.error(t('membersManager.removeFailed')),
  })

  const busy = addMutation.isPending || removeMutation.isPending

  const toggleAdd = (id: number) => {
    setToAdd((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <Dialog.Root open={group !== null} onOpenChange={(o) => { if (!o && !busy) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ animationDuration: '200ms' }} />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98]" style={{ animationDuration: '250ms', animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
          {/* Header */}
          <div className="flex items-start justify-between gap-3 border-b border-border/60 px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="text-sm font-semibold truncate">
                {t('membersManager.title', { name: group?.name ?? '' })}
              </Dialog.Title>
              <Dialog.Description className="mt-0.5 text-xs text-muted-foreground">
                {t('membersManager.description')}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" disabled={busy}>
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            {/* Current members */}
            <div className="px-5 py-4">
              <h3 className="mb-2 text-xs font-medium text-muted-foreground">
                {t('membersManager.inThisGroup', { count: members.length })}
              </h3>
              {members.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('membersManager.noMembers')}</p>
              ) : (
                <ul className="space-y-1.5">
                  {members.map((m) => {
                    const removing = removeMutation.isPending && removeMutation.variables?.id === m.id
                    return (
                      <li key={m.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2">
                        <Thumb src={m.latest_detection_thumbnail} />
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">{m.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 shrink-0 text-xs text-destructive hover:text-destructive"
                          onClick={() => group && removeMutation.mutate({ groupId: group.id, id: m.id })}
                          disabled={busy}
                        >
                          {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          <span className="ml-1.5">{t('membersManager.remove')}</span>
                        </Button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {/* Add cameras */}
            <div className="border-t border-border/60 bg-muted/20 px-5 py-4">
              <h3 className="mb-2 text-xs font-medium text-muted-foreground">{t('membersManager.addSection')}</h3>
              <div className="relative mb-2.5">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('membersManager.searchPlaceholder')}
                  aria-label={t('membersManager.searchPlaceholder')}
                  className="h-9 pl-10 text-sm"
                />
              </div>

              {candidates.length === 0 ? (
                <p className="py-2 text-sm text-muted-foreground">
                  {allCameras.filter((c) => !memberIds.has(c.id)).length === 0
                    ? t('membersManager.noCandidates')
                    : t('membersManager.noMatches')}
                </p>
              ) : (
                <ul className="max-h-64 space-y-1 overflow-y-auto">
                  {candidates.map((c) => {
                    const checked = toAdd.has(c.id)
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => toggleAdd(c.id)}
                          disabled={busy}
                          aria-pressed={checked}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors disabled:opacity-50',
                            checked ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted',
                          )}
                        >
                          <span className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                            checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                          )}>
                            {checked && <Check className="h-3 w-3" />}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm">{c.name}</span>
                          {c.group_name ? (
                            <span className="shrink-0 text-xs text-amber-600 dark:text-amber-500">
                              {t('membersManager.inGroupMoves', { group: c.group_name })}
                            </span>
                          ) : (
                            <span className="shrink-0 text-xs text-muted-foreground">{t('membersManager.ungrouped')}</span>
                          )}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
              <p className="mt-2.5 text-[11px] text-muted-foreground">{t('membersManager.moveHint')}</p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-2 border-t border-border/60 bg-muted/20 px-5 py-3">
            <Dialog.Close asChild>
              <Button variant="ghost" size="sm" className="h-9 text-xs" disabled={busy}>
                {t('membersManager.close')}
              </Button>
            </Dialog.Close>
            <Button
              size="sm"
              className="h-9 gap-1.5 text-xs"
              onClick={() => group && toAdd.size > 0 && addMutation.mutate({ groupId: group.id, ids: Array.from(toAdd) })}
              disabled={busy || toAdd.size === 0}
            >
              {addMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              {t('membersManager.addAction', { count: toAdd.size })}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function Thumb({ src }: { src: string | null }) {
  if (!src) {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-muted">
        <Camera className="h-4 w-4 text-muted-foreground/40" />
      </div>
    )
  }
  return <img src={src} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
}
